import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveImage } from "@/lib/image-map";
import type { Product, Creator } from "@/lib/queries";

type EmbedSearch = { limit: number; category: string; theme: "light" | "dark" };

export const Route = createFileRoute("/embed/$handle")({
  validateSearch: (search: Record<string, unknown>): EmbedSearch => {
    const rawLimit = Number(search.limit);
    const limit = Number.isFinite(rawLimit)
      ? Math.max(1, Math.min(48, Math.floor(rawLimit)))
      : 6;
    const category =
      typeof search.category === "string" && search.category ? search.category : "all";
    const theme: "light" | "dark" = search.theme === "dark" ? "dark" : "light";
    return { limit, category, theme };
  },
  component: EmbedPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex,nofollow" },
      { name: "viewport", content: "width=device-width,initial-scale=1" },
    ],
  }),
});

type EmbedTheme = {
  bg: string;
  fg: string;
  muted: string;
  card: string;
  border: string;
  btnBg: string;
  btnFg: string;
};

const THEMES: Record<"light" | "dark", EmbedTheme> = {
  light: {
    bg: "#ffffff",
    fg: "#0f0f0f",
    muted: "#6b6b6b",
    card: "#ffffff",
    border: "#e6e6e6",
    btnBg: "#0f0f0f",
    btnFg: "#ffffff",
  },
  dark: {
    bg: "#0b0b0b",
    fg: "#f5f5f5",
    muted: "#9a9a9a",
    card: "#141414",
    border: "#2a2a2a",
    btnBg: "#f5f5f5",
    btnFg: "#0b0b0b",
  },
};

async function fetchEmbedData(handle: string, limit: number, category: string) {
  const { data: creator } = await (supabase as any)
    .from("public_creators")
    .select("*")
    .eq("handle", handle)
    .maybeSingle();
  if (!creator) return { creator: null as Creator | null, products: [] as Product[] };

  let query = supabase
    .from("creator_products")
    .select("position, products(*)")
    .eq("creator_id", (creator as Creator).id)
    .order("position")
    .limit(limit);

  const rows = await query;
  let products = (rows.data ?? [])
    .map((r: any) => r.products as Product)
    .filter(Boolean);

  if (category && category !== "all") {
    products = products.filter(
      (p) => (p.category ?? "").toLowerCase() === category.toLowerCase(),
    );
  }

  return { creator: creator as Creator, products: products.slice(0, limit) };
}

function formatPrice(p: Product): string {
  const amount =
    typeof p.price_cents === "number" && p.price_cents > 0
      ? p.price_cents / 100
      : p.price;
  if (!amount && amount !== 0) return "";
  const currency = (p.currency || "GBP").toUpperCase();
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function EmbedPage() {
  const { handle } = Route.useParams();
  const { limit, category, theme } = Route.useSearch();
  const t = THEMES[theme as "light" | "dark"];

  const { data, isLoading } = useQuery({
    queryKey: ["embed", handle, limit, category],
    queryFn: () => fetchEmbedData(handle, limit, category),
  });

  const containerStyle: React.CSSProperties = {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    background: t.bg,
    color: t.fg,
    padding: "16px",
    minHeight: "100vh",
    boxSizing: "border-box",
  };

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <p style={{ color: t.muted, fontSize: 14 }}>Loading…</p>
      </div>
    );
  }

  if (!data || !data.creator) {
    return (
      <div style={containerStyle}>
        <p style={{ color: t.muted, fontSize: 14 }}>Storefront not found.</p>
      </div>
    );
  }

  const { creator, products } = data;

  return (
    <div style={containerStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 8,
        }}
      >
        <div style={{ fontSize: 13, color: t.muted }}>
          Curated by{" "}
          <a
            href={`https://www.pluggzofficial.com/creator/${creator.handle}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: t.fg, textDecoration: "none", fontWeight: 600 }}
          >
            @{creator.handle}
          </a>
        </div>
        <a
          href="https://www.pluggzofficial.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11, color: t.muted, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase" }}
        >
          Pluggz
        </a>
      </div>

      {products.length === 0 ? (
        <p style={{ color: t.muted, fontSize: 14 }}>No products yet.</p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          {products.map((p) => {
            const href = `https://www.pluggzofficial.com/r/${creator.handle}/${p.id}`;
            return (
              <a
                key={p.id}
                href={href}
                target="_blank"
                rel="noopener noreferrer sponsored"
                style={{
                  display: "block",
                  background: t.card,
                  border: `1px solid ${t.border}`,
                  textDecoration: "none",
                  color: t.fg,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    aspectRatio: "4 / 5",
                    background: t.bg,
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={resolveImage(p.image_key)}
                    alt={p.title}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                <div style={{ padding: 10 }}>
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      color: t.muted,
                      marginBottom: 4,
                    }}
                  >
                    {p.brand}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: 1.3,
                      marginBottom: 6,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {p.title}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {formatPrice(p)}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        background: t.btnBg,
                        color: t.btnFg,
                        padding: "6px 10px",
                        letterSpacing: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      Shop →
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
      {/* Keep Link import used to satisfy lint when unused */}
      <span style={{ display: "none" }}>
        <Link to="/">.</Link>
      </span>
    </div>
  );
}